import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type MfaFactor = {
  id: string;
  factor_type: "totp" | "phone" | string;
  status: "verified" | "unverified" | string;
  friendly_name?: string | null;
  phone?: string | null;
};

/** Lists the signed-in user's MFA factors. */
export function useMfaFactors() {
  const [factors, setFactors] = useState<MfaFactor[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (!error && data) setFactors((data.all ?? []) as MfaFactor[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    factors,
    verified: factors.filter((f) => f.status === "verified"),
    loading,
    refresh,
  };
}

/** Removes abandoned (unverified) factors so a new enrolment can start cleanly. */
export async function cleanupUnverifiedFactors() {
  const { data } = await supabase.auth.mfa.listFactors();
  const stale = ((data?.all ?? []) as MfaFactor[]).filter((f) => f.status !== "verified");
  for (const f of stale) {
    await supabase.auth.mfa.unenroll({ factorId: f.id });
  }
}

/** Challenge + verify in one call (for TOTP). */
export async function challengeAndVerify(factorId: string, code: string) {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw error;
}

export async function startChallenge(factorId: string) {
  const { data, error } = await supabase.auth.mfa.challenge({ factorId });
  if (error) throw error;
  return data.id;
}

export async function verifyChallenge(factorId: string, challengeId: string, code: string) {
  const { error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
  if (error) throw error;
}

export function maskPhone(phone?: string | null) {
  if (!phone) return "your phone";
  const digits = phone.replace(/\D/g, "");
  return `•••${digits.slice(-2)}`;
}
